/**
 * ContentReviewPanel — sidebar panel for content review (the canonical content
 * drawer; parallels ScriptReviewPanel in ../ScriptReview).
 *
 * Used by: ContentTab (interactive review) and ConfirmedTab (read-only audit).
 * Always renders as a full-height right-side panel; no modal mode.
 *
 * Layout: video + metadata | AI review tabs | optional thread column.
 * ChatThread is the shared common component — same as in ScriptReviewPanel
 * and ReviewThreadDrawer (creator side-panel). In anchored-notes mode the
 * right column is that same ChatThread, plus the moment props.
 *
 * The pieces it composes live beside it: `PanelHeader` (top bar),
 * `VideoSurface` (which video surface to show), `useAnchoredNotesPanel`
 * (all anchored-notes state) and `useNotesChatProps` (the chat's moment and file props).
 */
import React from 'react';
import type { AssignedProduct } from '@discovr/types';
import { Sheet, SheetContent, SheetTitle } from '../../ui/sheet';
import { Check, ExternalLink } from '../../../icons';
import { cn } from '../../../lib/utils';
import { ChatThread, type ChatThreadProps } from '../ChatThread';
import { ContentResultTabs } from './ContentResultTabs';
import { ActionConfirmDialog } from '../ActionConfirmDialog';
import { PanelHeader } from './PanelHeader';
import { VideoSurface } from './VideoSurface';
import { useAnchoredNotesPanel, type ContentNotesMode } from './useAnchoredNotesPanel';
import { useNotesChatProps, useThreadDraft, useThreadDraftStore, type NotesChatProps, type ThreadDraftStore } from './useNotesChatProps';
import {
  FocusToggle,
  panelWidthStyle,
  playerFitMaxWidth,
  Splitter,
  UNSAVED_FILES,
  UNSAVED_FRAME,
  UNSAVED_FRAME_AND_FILES,
  type UnsavedWork,
} from './notes';
import type { ContentReviewBody, GeoAiEnvelope } from './types';
import type { ReviewThreadEntry } from '../../../lib/models';
import type { CampaignSearch } from '../../../campaign-detail/search';

export type { ContentNotesMode } from './useAnchoredNotesPanel';

const APPROVED_ICON_SIZE = 18;
const ALT_LINK_ICON_SIZE = 12;

// Every non-notes surface: a video column that never compresses beside the
// AI tabs, and a chat column that slides in at a fixed width.
const LEGACY_VIDEO_COLUMN_PX = 420;
const LEGACY_CHAT_COLUMN_PX = 360;
const LEGACY_BODY_GRID_STYLE: React.CSSProperties = { gridTemplateColumns: `${LEGACY_VIDEO_COLUMN_PX}px minmax(0,1fr)` };
const LEGACY_CHAT_INNER_STYLE: React.CSSProperties = { width: LEGACY_CHAT_COLUMN_PX };
const COLLAPSED_WIDTH_PX = 0;

// The two heavy columns re-render only when their own props change, not on
// every panel render (a splitter release, the close prompt, the header).
const MemoContentResultTabs = React.memo(ContentResultTabs);

const RESIZE_PLAYER_LABEL = 'Resize the video column';
const RESIZE_NOTES_LABEL = 'Resize the notes column';

const SETTLED_STATUS_PATTERN = /live|approved/i;
const SETTLED_STATUS_CLASSES = 'bg-emerald-50 text-emerald-700';
const IN_REVIEW_STATUS_CLASSES = 'bg-(--brand-500)/10 text-(--brand-500)';

function statusPillClasses(statusLabel: string): string {
  if (SETTLED_STATUS_PATTERN.test(statusLabel)) return SETTLED_STATUS_CLASSES;
  return IN_REVIEW_STATUS_CLASSES;
}

/** The chat column animates its width open and shut; its content keeps the full width. */
function legacyChatColumnStyle(threadOpen: boolean): React.CSSProperties {
  return { width: threadOpen ? LEGACY_CHAT_COLUMN_PX : COLLAPSED_WIDTH_PX };
}

// The close guard's prompt, by what closing would lose.
const DISCARD_FRAME_TITLE = 'Discard the captured frame?';
const DISCARD_FRAME_DESCRIPTION = 'Closing now will lose the frame you captured. Your note text is kept.';
const DISCARD_FILES_TITLE = 'Discard the attached files?';
const DISCARD_FILES_DESCRIPTION = 'Closing now will remove the files you attached.';
const DISCARD_FRAME_AND_FILES_TITLE = 'Discard the attached frame and files?';
const DISCARD_FRAME_AND_FILES_DESCRIPTION = 'Closing now will lose the frame you captured and the files you attached.';
const DISCARD_AND_CLOSE_LABEL = 'Discard and close';

interface DiscardCopy {
  title: string;
  description: string;
}

function discardCopyFor(unsaved: UnsavedWork): DiscardCopy {
  switch (unsaved) {
    case UNSAVED_FRAME:
      return { title: DISCARD_FRAME_TITLE, description: DISCARD_FRAME_DESCRIPTION };
    case UNSAVED_FILES:
      return { title: DISCARD_FILES_TITLE, description: DISCARD_FILES_DESCRIPTION };
    case UNSAVED_FRAME_AND_FILES:
      return { title: DISCARD_FRAME_AND_FILES_TITLE, description: DISCARD_FRAME_AND_FILES_DESCRIPTION };
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ContentReviewPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  creatorName: string;
  creatorAvatarUrl?: string;
  creatorHandle?: string;

  /** Products assigned to this creator (Multi-Product Campaigns), resolved from
   *  ContentDetail.assigned_products. Rendered as read-only "Featuring" chips in
   *  the header — identical for brand and admin. */
  assignedProducts?: AssignedProduct[];
  /** Campaign product-catalog size — gates the Featuring chips to >= 2 products
   *  (0-1 product campaigns render no chips, exactly as before). */
  campaignProductCount?: number;

  /** Roster entry id — the id the Regenerate/Rephrase/Select GEO mutations
   *  post to. Threaded to ContentResultTabs (as `geoEntryId`); this panel
   *  makes no network calls of its own with it. */
  entryId?: string;
  /** GEO finalize envelope (ContentDetail.geo_finalize) — real chapter
   *  timestamps + a publish-ready description stitched onto the uploaded
   *  video's transcript. Threaded straight into ContentResultTabs' GEO tab;
   *  this panel makes no network calls of its own. Mirrors ScriptReviewPanel's
   *  `geoAnalysis` prop. */
  geoAnalysis?: GeoAiEnvelope;
  /** Show the "GEO Optimized" tab. Gate to YouTube campaigns at the call
   *  site — mirrors ScriptReviewPanel's `showGeo`. */
  showGeo?: boolean;
  /** Regenerate the whole GEO finalize pack — wired by the caller (which owns
   *  the actual mutation); this panel just threads the callback through to
   *  ContentResultTabs. Hidden there when `geoReadOnly` or when this prop
   *  isn't provided at all. May return a Promise — ContentResultTabs' GEO tab
   *  awaits it to drive a local busy/spinner state. */
  onRegenerateGeoFinalize?: () => void | Promise<void>;
  /** Rephrase a single finalized element — wired by the caller, same pattern
   *  as `onRegenerateGeoFinalize`. */
  onRephraseGeoElement?: (element: 'chapters' | 'description') => void | Promise<void>;
  /** Save the FULL next finalize-selection array — wired by the caller, same
   *  pattern as `onRegenerateGeoFinalize`. ContentResultTabs' GEO tab computes
   *  the next array from its own local selection state and passes the whole
   *  thing, not just the toggled key. */
  onSaveGeoSelection?: (selected: string[]) => void | Promise<void>;
  /** Read-only GEO surface (Confirmed/finalized tab): GEO finalize still
   *  auto-runs on open, but the manual Re-finalize button is hidden.
   *  Defaults to false so live review callers are unaffected. */
  geoReadOnly?: boolean;

  /** Platform username for the admin share deep link (distinct from the
   *  presentational `creatorHandle`, which may be the display name). */
  shareCreatorUsername?: string;
  /** Override the tab embedded in the share URL (defaults to 'content').
   *  Pass 'confirmed' when rendering this panel from the Finalized tab. */
  shareTab?: NonNullable<CampaignSearch['tab']>;
  /** Override the panel value embedded in the share URL (defaults to
   *  'content-review' for the live review surface). Pass 'content-finalized'
   *  when rendering this panel from the Finalized tab so a shared link routes
   *  back to the read-only surface — not the live workflow review. */
  sharePanel?: 'content-review' | 'content-finalized';
  statusLabel: string;
  updatedLabel: string;
  costLabel?: string;
  // Video
  videoUrl?: string;
  videoSecondaryUrl?: string;
  /** True when the content URL is a Drive link and the S3 video isn't ready yet.
   *  Shows a processing spinner in place of the video embed. */
  videoProcessing?: boolean;
  // Optional content below the video
  leftExtras?: React.ReactNode;
  // Approved ribbon
  showApprovedBanner?: boolean;
  approvedBannerText?: string;
  // Review action (Approve button in header)
  canReview: boolean;
  reviewBusy?: boolean;
  isApproved?: boolean;
  onApprove: () => void;
  // Thread column (right panel toggle)
  thread?: ReviewThreadEntry[];
  unreadCount?: number;
  canAddComment?: boolean;
  /** Thread is locked (approved -> read-only). Forwarded to ChatThread so
   *  Edit/Delete gate on the LOCK rather than on `canAddComment`, which at some
   *  call sites also encodes "is a post handler wired?" and would otherwise
   *  strip both affordances from an open thread. Omitted: ChatThread falls back
   *  to `canAddComment === false`. */
  locked?: boolean;
  addCommentBusy?: boolean;
  onAddComment?: (body: string, replyTo?: string) => void | Promise<void>;
  onEditMessage?: (id: string, body: string) => void | Promise<void>;
  onDeleteMessage?: (id: string) => void | Promise<void>;
  /** True only for the Super Admin tier — lets ChatThread's delete
   *  affordance bypass the locked-thread gate (server permission flags
   *  already scope who may actually delete what). */
  viewerIsSuperAdmin?: boolean;
  viewerActor?: string;
  // AI review result
  aiAnalysis?: {
    status: 'pending' | 'complete' | 'failed';
    data?: Record<string, unknown>;
    error?: string;
  };
  /** 'admin' shows real failure states; 'brand' shows softened "still preparing" banner. */
  audience?: 'brand' | 'admin';
  /** Called when the user expands the thread section. Use to fire mark-read. */
  onThreadOpen?: () => void;
  /** Called whenever the thread column toggles (open or close). Use to mirror
   *  the state to `?thread=open` so refresh restores the panel identically. */
  onThreadOpenChange?: (open: boolean) => void;
  /** Deep-link hint: start with the thread column already expanded
   *  (e.g. `?panel=content-review&thread=open`). Falls back to the existing
   *  "open when the thread already has messages" behaviour. */
  defaultThreadOpen?: boolean;
  /** D5: when true, the conversation toggle button itself is not rendered
   *  at all (not just disabled) — used when a public grant's
   *  allow_chat_readonly was off at mint time, so there is genuinely
   *  nothing to show, not an empty state to reveal. Defaults false — every
   *  existing authed caller is unaffected. */
  hideThreadToggle?: boolean;
  /**
   * Anchored notes mode. When set, the panel mounts the native MediaTheater
   * instead of VideoEmbed, and the ChatThread column gains the moment props.
   * Only pass it when `isNativeReviewable(videoUrl)` is true — every other
   * surface must render exactly as before.
   */
  notes?: ContentNotesMode;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ContentReviewPanel({
  open,
  onClose,
  title,
  creatorName,
  creatorAvatarUrl,
  creatorHandle,
  assignedProducts,
  campaignProductCount,
  entryId,
  geoAnalysis,
  showGeo,
  onRegenerateGeoFinalize,
  onRephraseGeoElement,
  onSaveGeoSelection,
  geoReadOnly,
  shareCreatorUsername,
  shareTab: _shareTab = 'content',
  sharePanel = 'content-review',
  statusLabel,
  updatedLabel,
  costLabel,
  videoUrl,
  videoSecondaryUrl,
  videoProcessing = false,
  leftExtras,
  showApprovedBanner,
  approvedBannerText,
  canReview,
  reviewBusy,
  isApproved,
  onApprove,
  thread,
  unreadCount,
  canAddComment,
  locked,
  addCommentBusy,
  onAddComment,
  onEditMessage,
  onDeleteMessage,
  viewerIsSuperAdmin,
  viewerActor = 'brand',
  aiAnalysis,
  audience = 'admin',
  onThreadOpen,
  onThreadOpenChange,
  defaultThreadOpen = false,
  hideThreadToggle = false,
  notes,
}: ContentReviewPanelProps) {
  const [threadOpen, setThreadOpen] = React.useState<boolean>(
    () => defaultThreadOpen || (Array.isArray(thread) && thread.length > 0),
  );
  // Held here so the draft outlives the chat's remount when the player becomes
  // native; read by `ReviewChatThread`, so typing never re-renders the panel.
  const threadDraft = useThreadDraftStore(notes?.initialText);
  const threadDraftRef = React.useRef<HTMLTextAreaElement>(null);

  const setThreadVisibility = React.useCallback(
    (nextOpen: boolean) => {
      setThreadOpen(nextOpen);
      if (nextOpen) onThreadOpen?.();
      onThreadOpenChange?.(nextOpen);
    },
    [onThreadOpen, onThreadOpenChange],
  );

  // "Note at" and a note marker both act on the chat, so it must be showing first.
  const revealConversation = React.useCallback(() => {
    if (threadOpen) return;
    setThreadVisibility(true);
  }, [threadOpen, setThreadVisibility]);

  const anchored = useAnchoredNotesPanel({
    notes,
    entryId,
    videoUrl,
    aiData: aiAnalysis?.data,
    thread,
    threadOpen,
    onClose,
    onRevealConversation: revealConversation,
  });
  const {
    notesEnabled,
    controller,
    session,
    columns,
    corsMode,
    nativeSrc,
    videoLoadFailed,
    retryFailedVideo,
    seekFlashNonce,
    focusMode,
    toggleFocusMode,
    reviewColumnHidden,
    gridRef,
    gridTemplateColumns,
    guardedClose,
    handleSheetEscape,
    discardPrompt,
    dismissDiscardPrompt,
  } = anchored;

  const notesChatProps = useNotesChatProps(session, notes);

  const toggleThread = React.useCallback(() => {
    setThreadVisibility(!threadOpen);
  }, [threadOpen, setThreadVisibility]);

  // AI flags → "Note at …": the conversation must be showing before the
  // session stages the timestamp and focuses the chat input.
  const { pinToTime } = session;
  const noteAt = React.useCallback(
    (tMs: number) => {
      revealConversation();
      pinToTime(tMs);
    },
    [revealConversation, pinToTime],
  );

  const handleSheetOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) guardedClose();
    },
    [guardedClose],
  );

  const confirmDiscard = React.useCallback(() => {
    dismissDiscardPrompt();
    onClose();
  }, [dismissDiscardPrompt, onClose]);

  const handleDiscardPromptOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) dismissDiscardPrompt();
    },
    [dismissDiscardPrompt],
  );

  const discardCopy = discardCopyFor(discardPrompt.unsaved);

  const submitComment = React.useMemo(() => {
    if (!onAddComment) return undefined;
    return ({ body, replyTo }: { body: string; replyTo?: string }) => onAddComment(body, replyTo);
  }, [onAddComment]);

  if (!open || typeof document === 'undefined') return null;

  // One ChatThread for both paths, so the notes column can never drift from
  // the plain one: notes mode only layers the moment and file props on top
  // (the rich composer, the file source and the viewer's open state).
  function renderChatThread(notesProps?: NotesChatProps) {
    return (
      <ReviewChatThread
        draftStore={threadDraft}
        entries={thread}
        canAddComment={canAddComment}
        locked={locked}
        addCommentBusy={addCommentBusy}
        onAddComment={submitComment}
        onEditMessage={onEditMessage}
        onDeleteMessage={onDeleteMessage}
        viewerIsSuperAdmin={viewerIsSuperAdmin}
        enableReplies
        outgoingActor={viewerActor}
        textareaRef={threadDraftRef}
        {...notesProps}
      />
    );
  }

  // "Note at …" only exists in notes mode, where the session owns the
  // seek/stage/focus, and only for a viewer who can send right now — a
  // read-only or locked thread would stage a moment that can never go out.
  // Other surfaces get `undefined`, same as every other notes-only callback
  // threaded through here.
  const canNoteAt = notesEnabled && !!notes?.canWrite && !notes?.locked;
  const noteAtHandler = canNoteAt ? noteAt : undefined;

  // Plain inline style, not a Tailwind arbitrary-value class — Tailwind's JIT
  // never generates a rule for a nested min(max(...),calc(...)) class (no
  // matching rule appears in any loaded stylesheet), so the panel silently
  // fell back to auto/content width. sm:max-w-none max-[992px]:w-full! below
  // still wins on narrow viewports via !important, which beats inline style.
  // Fixed-px floors (not just vw-percentage) so the tab row — up to 7 tabs
  // incl. GEO Optimized — always has room: video column (420) + tabs column
  // (820) = 1240 min; +360 chat column when the thread is open.
  // The same constants drive `panelAvailableWidth`, which clamps the notes-mode
  // columns — one formula, two readers.
  const panelWidth = panelWidthStyle(threadOpen);

  // Video + metadata — no scroll; stays fixed while AI tabs scroll.
  // Notes mode: the column scrolls and its content is height-fitted so the
  // transport stays in view. In focus mode that same fit is what centres the
  // video in a full-width column, over the muted rail background.
  const playerColumn = (
    <div className={cn('min-h-0 bg-card', notesEnabled ? 'overflow-y-auto' : 'overflow-hidden', focusMode && 'bg-muted')}>
      <div
        className={cn('flex flex-col gap-5 px-6 pb-6 pt-5', notesEnabled && 'mx-auto w-full', focusMode && 'pt-6')}
        style={notesEnabled ? { maxWidth: playerFitMaxWidth(controller.intrinsic) } : undefined}
      >
        <div className="space-y-2">
          <VideoSurface
            videoProcessing={videoProcessing}
            notes={notesEnabled}
            videoUrl={videoUrl}
            nativeSrc={nativeSrc}
            videoLoadFailed={videoLoadFailed}
            onRetryVideo={retryFailedVideo}
            session={session}
            controller={controller}
            creatorName={creatorName}
            seekFlashNonce={seekFlashNonce}
            corsMode={corsMode}
          />
          {videoSecondaryUrl && videoSecondaryUrl !== videoUrl && (
            <a
              href={videoSecondaryUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex w-fit items-center gap-1.5 text-caption-medium text-muted-foreground hover:text-violet-700"
            >
              <ExternalLink size={ALT_LINK_ICON_SIZE} className="text-muted-foreground" />
              Open alternate link
            </a>
          )}
        </div>

        {/* Metadata strip — Figma KPI 1: p-6, border, rounded-lg */}
        <div className={cn('grid justify-between rounded-lg border border-border bg-card p-6', costLabel ? 'grid-cols-3' : 'grid-cols-2')}>
          <MetaField label="Status">
            <span className={cn('inline-flex items-center rounded-full px-3 py-1.5 text-caption-medium', statusPillClasses(statusLabel))}>
              {statusLabel}
            </span>
          </MetaField>
          <MetaField label="Updated">
            <span className="text-caption-medium tabular-nums text-foreground">{updatedLabel}</span>
          </MetaField>
          {costLabel && (
            <MetaField label="Cost">
              <span className="text-caption-medium tabular-nums text-foreground">{costLabel}</span>
            </MetaField>
          )}
        </div>

        {/* leftExtras (ContentRowDetail, embed) owns its own border and
            returns null when it has nothing to show — so no empty card. */}
        {leftExtras}

        {showApprovedBanner && (
          <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200/80 bg-emerald-50/90 px-4 py-3">
            <Check size={APPROVED_ICON_SIZE} className="shrink-0 text-emerald-600" strokeWidth={2.5} />
            <span className="text-body-sm-semibold text-emerald-900">
              {approvedBannerText ?? 'Approved'}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  // AI review tabs — scrolls independently within its bounded column.
  // No overflow/pb-5 here: ContentResultTabs handles its own internal split
  // (shrink-0 tab bar + a scrolling region below it, with the bottom padding
  // living inside that region), so its vertical scrollbar runs alongside the
  // tab content only, not the tab bar above it.
  // Focus mode hides the column rather than unmounting it, so the reviewer
  // comes back to the tab they were reading.
  // Full view is a modal dialog over the player: the columns beside it must not be reachable behind it.
  const fullViewOpen = notesEnabled && session.fullView;
  const aiColumn = (
    <div className={cn('flex h-full min-h-0 flex-col bg-card', reviewColumnHidden && 'hidden')} hidden={reviewColumnHidden} inert={fullViewOpen}>
      <MemoContentResultTabs
        data={aiAnalysis?.data as ContentReviewBody | undefined}
        aiStatus={aiAnalysis?.status}
        aiError={aiAnalysis?.error}
        audience={audience}
        geoEntryId={entryId}
        geoAnalysis={geoAnalysis}
        showGeo={showGeo}
        onRegenerateGeoFinalize={onRegenerateGeoFinalize}
        onRephraseGeoElement={onRephraseGeoElement}
        onSaveGeoSelection={onSaveGeoSelection}
        geoReadOnly={geoReadOnly}
        assignedProducts={assignedProducts}
        campaignProductCount={campaignProductCount}
        onNoteAt={noteAtHandler}
      />
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={handleSheetOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        aria-label={title}
        style={panelWidth}
        className="min-h-0 gap-0 border-l border-border bg-card p-0 transition-[width] duration-300 ease-in-out sm:max-w-none max-[992px]:w-full!"
        onEscapeKeyDown={handleSheetEscape}
        // Inert everywhere except the public share shell, which reads this
        // attribute in its click-capture allow-list (PublicCampaignShell.tsx)
        // to let every read-only interaction inside THIS granted panel work
        // normally on a public link — see that file's header. Radix's own
        // overlay-click/Escape dismissal (onOpenChange above) is unaffected
        // either way — it never goes through React's click-capture at all.
        data-public-allowed-subtree=""
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>

        {/* Full-width top bar — creator info (left) + actions (right). Stretches
            as the panel widens; the conversation spawns in from the right below
            it. No section labels (the drawer is self-evident). */}
        <PanelHeader
          creatorName={creatorName}
          creatorAvatarUrl={creatorAvatarUrl}
          creatorHandle={creatorHandle}
          statusLabel={statusLabel}
          canReview={canReview}
          reviewBusy={reviewBusy}
          isApproved={isApproved}
          onApprove={onApprove}
          hideThreadToggle={hideThreadToggle}
          threadOpen={threadOpen}
          onToggleThread={toggleThread}
          unreadCount={unreadCount}
          shareCreatorUsername={shareCreatorUsername}
          sharePanel={sharePanel}
          onClose={guardedClose}
        />

        {/* Body: review (left) | conversation (right), divided by a vertical
            border. The conversation spawns in from the right, pushing the review
            content to the left.
            Notes mode instead lays the three columns out as one resizable grid,
            so nothing here may scroll horizontally — below the three-column
            width the columns give way rather than push the panel wider. */}
        <div className={cn('flex min-h-0 flex-1', notesEnabled ? 'overflow-hidden' : 'overflow-x-auto overflow-y-hidden')}>
          {notesEnabled ? (
            <div
              className={cn(
                'grid min-h-0 flex-1',
                // The transition is what animates the conversation open/closed;
                // during a drag it would lag a frame behind the pointer.
                !columns.dragging && 'transition-[grid-template-columns] duration-300 ease-in-out',
              )}
              ref={gridRef}
              style={{ gridTemplateColumns }}
              data-testid="notes-layout"
            >
              {playerColumn}
              <Splitter
                column="player"
                widthPx={columns.playerW}
                maxWidthPx={columns.playerMaxW}
                label={RESIZE_PLAYER_LABEL}
                dragging={columns.dragColumn === 'player'}
                disabled={focusMode}
                onResizeStart={(clientX) => columns.startResize('player', clientX)}
                onResizeMove={columns.moveResize}
                onResizeEnd={columns.endResize}
                onReset={() => columns.resetColumn('player')}
                onNudge={(deltaPx) => columns.nudgeColumn('player', deltaPx)}
              >
                {/* With the conversation closed this splitter IS the panel's
                    last column, so the button has to tuck inward or the body's
                    `overflow-hidden` clips half of it away. */}
                <FocusToggle focused={focusMode} atPanelEdge={focusMode && !threadOpen} onToggle={toggleFocusMode} />
              </Splitter>
              {aiColumn}
              {/* In focus mode the first splitter already divides player from
                  notes, so this one has no columns left to sit between. */}
              {!focusMode && (
                <Splitter
                  column="notes"
                  widthPx={columns.notesW}
                  maxWidthPx={columns.notesMaxW}
                  label={RESIZE_NOTES_LABEL}
                  dragging={columns.dragColumn === 'notes'}
                  collapsed={!threadOpen}
                  onResizeStart={(clientX) => columns.startResize('notes', clientX)}
                  onResizeMove={columns.moveResize}
                  onResizeEnd={columns.endResize}
                  onReset={() => columns.resetColumn('notes')}
                  onNudge={(deltaPx) => columns.nudgeColumn('notes', deltaPx)}
                />
              )}
              {/* Conversation column — a grid track, not a slide-in overlay;
                  the conversation toggle animates its track to 0px. */}
              <div className="flex h-full min-h-0 flex-col overflow-hidden bg-card" inert={fullViewOpen}>
                {renderChatThread(notesChatProps)}
              </div>
            </div>
          ) : (
            <>
              {/* Review body: video + metadata | AI review tabs. Always side-by-side.
                  Each column is independently bounded — only the AI-tabs column scrolls.
                  The video column is FIXED — it never compresses. The tabs
                  column takes the remaining space and can shrink freely; its own tab
                  row (not this whole column) scrolls horizontally when the tab labels
                  don't fit, so the video content is never squeezed. */}
              <div className="grid min-h-0 flex-1 divide-x divide-border" style={LEGACY_BODY_GRID_STYLE}>
                {playerColumn}
                {aiColumn}
              </div>

              {/* Chat column — always mounted so closing animates out smoothly.
                  overflow-hidden + width transition slides it in from the right.
                  Inner div holds fixed width so ChatThread doesn't reflow mid-animation. */}
              <div
                className={cn(
                  'shrink-0 h-full overflow-hidden bg-card transition-[width] duration-300 ease-in-out',
                  threadOpen && 'border-l border-border',
                )}
                style={legacyChatColumnStyle(threadOpen)}
              >
                <div className="flex h-full min-h-0 flex-col" style={LEGACY_CHAT_INNER_STYLE}>
                  {renderChatThread()}
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
      <ActionConfirmDialog
        open={discardPrompt.open}
        onOpenChange={handleDiscardPromptOpenChange}
        variant="delete"
        title={discardCopy.title}
        description={discardCopy.description}
        confirmLabel={DISCARD_AND_CLOSE_LABEL}
        onConfirm={confirmDiscard}
      />
    </Sheet>
  );
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

interface ReviewChatThreadProps extends Omit<ChatThreadProps, 'draft' | 'setDraft'> {
  draftStore: ThreadDraftStore;
}

/**
 * The shared ChatThread, reading its draft from the panel's store: a keystroke
 * re-renders this chat alone, and a panel re-render skips it unless its own
 * props change.
 */
const ReviewChatThread = React.memo(function ReviewChatThread({ draftStore, ...chatProps }: ReviewChatThreadProps) {
  const [draft, setDraft] = useThreadDraft(draftStore);
  return <ChatThread {...chatProps} draft={draft} setDraft={setDraft} />;
});

function MetaField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <dt className="text-caption-regular/4 font-medium text-muted-foreground">{label}</dt>
      <dd className="flex min-h-7 items-center">{children}</dd>
    </div>
  );
}
